/* eslint-disable import/no-cycle */
import { lazy, Suspense } from 'react';
import { Form, Skeleton } from 'antd';

const Input = lazy(() => import('./input'));
const Rate = lazy(() => import('./rate'));
const Select = lazy(() => import('./select'));
const TimePickerRange = lazy(() => import('./timepicker-range'));
const DatePicker = lazy(() => import('./datepicker'));
const Upload = lazy(() => import('./upload'));
const InputMultiLang = lazy(() => import('./input-multilang'));
const InputNumber = lazy(() => import('./input-number'));
const DatePickerRange = lazy(() => import('./datepicker-range'));
const TextArea = lazy(() => import('./text-area'));

/**
 * @typedef {Object} ControlBaseProps
 * @property {'input'|'select'|'rate'|'upload'|'datepicker'|'timepicker-range'|'input-multilang'|'textarea'|'input-number'|'datepicker-range'} type - The type of control to render
 * @property {string} [label] - Label for the form control
 * @property {string|number|Array} [name] - Name attribute for the form control
 * @property {boolean} [required] - Whether the field is required
 * @property {string} [placeholder] - Placeholder text for the input
 * @property {boolean} [disabled] - Whether the control is disabled
 * @property {any} [value] - Current value of the control
 * @property {Function} [onChange] - Callback when value changes
 * @property {Function} [onBlur] - Callback when control loses focus
 * @property {Function} [onFocus] - Callback when control gains focus
 */

/**
 * @typedef {ControlBaseProps & import('antd').InputProps} InputControlProps
 * @property {string} [type] - HTML input type (text, password, email, etc.)
 * @property {number} [maxLength] - Maximum number of characters
 * @property {string} [prefix] - Prefix icon or text
 * @property {string} [suffix] - Suffix icon or text
 * @property {boolean} [allowClear] - Whether to show clear button
 * @property {boolean} [readOnly] - Whether the input is read-only
 */

/**
 * @typedef {ControlBaseProps & import('antd').SelectProps} SelectControlProps
 * @property {Array} [options] - Array of option objects
 * @property {boolean} [multiple] - Whether multiple selection is allowed
 * @property {boolean} [allowClear] - Whether to show clear button
 * @property {boolean} [showSearch] - Whether to show search input
 * @property {Function} [onSearch] - Callback when searching
 * @property {Function} [onSelect] - Callback when option is selected
 * @property {Function} [onDeselect] - Callback when option is deselected
 */

/**
 * @typedef {ControlBaseProps & import('antd').RateProps} RateControlProps
 * @property {number} [count] - Number of stars to display
 * @property {number} [defaultValue] - Default rating value
 * @property {boolean} [allowHalf] - Whether to allow half stars
 * @property {boolean} [allowClear] - Whether to allow clearing the rating
 * @property {Array} [character] - Custom character for each star
 */

/**
 * @typedef {ControlBaseProps & import('antd').UploadProps} UploadControlProps
 * @property {string} [action] - Upload URL
 * @property {Array} [accept] - Accepted file types
 * @property {number} [maxCount] - Maximum number of files
 * @property {number} [maxSize] - Maximum file size in bytes
 * @property {boolean} [multiple] - Whether to allow multiple files
 * @property {Function} [beforeUpload] - Function called before upload
 * @property {Function} [onChange] - Callback when file list changes
 */

/**
 * @typedef {ControlBaseProps & import('antd').DatePickerProps} DatePickerControlProps
 * @property {string} [format] - Date format string
 * @property {boolean} [showTime] - Whether to show time picker
 * @property {boolean} [allowClear] - Whether to show clear button
 * @property {boolean} [disabled] - Whether the picker is disabled
 * @property {Function} [onChange] - Callback when date changes
 */

/**
 * @typedef {ControlBaseProps & Object} TimePickerRangeControlProps
 * @property {Array} [value] - Array of two moment objects [start, end]
 * @property {string} [format] - Time format string
 * @property {boolean} [allowClear] - Whether to show clear button
 * @property {boolean} [disabled] - Whether the picker is disabled
 * @property {Function} [onChange] - Callback when time range changes
 */

/**
 * @typedef {ControlBaseProps & Object} InputMultiLangControlProps
 * @property {Object} [translations] - Object with language keys and values
 * @property {Array} [languages] - Array of supported languages
 * @property {string} [defaultLanguage] - Default language key
 * @property {Function} [onLanguageChange] - Callback when language changes
 */

/**
 * @typedef {ControlBaseProps & import('antd').InputNumberProps} InputNumberControlProps
 * @property {number} [min] - Minimum value
 * @property {number} [max] - Maximum value
 * @property {number} [step] - Step value for increment/decrement
 * @property {number} [precision] - Number of decimal places
 * @property {boolean} [keyboard] - Whether to enable keyboard navigation
 * @property {string} [formatter] - Function to format display value
 * @property {string} [parser] - Function to parse input value
 */

/**
 * @typedef {ControlBaseProps & import('antd').TextAreaProps} TextAreaControlProps
 * @property {number} [rows] - Number of rows
 * @property {number} [cols] - Number of columns
 * @property {number} [maxLength] - Maximum number of characters
 * @property {boolean} [autoSize] - Whether to auto-resize
 * @property {boolean} [showCount] - Whether to show character count
 */

/**
 * @typedef {ControlBaseProps & Object} DatePickerRangeControlProps
 * @property {Array} [value] - Array of two moment objects [start, end]
 * @property {string} [format] - Date format string
 * @property {boolean} [showTime] - Whether to show time picker
 * @property {boolean} [allowClear] - Whether to show clear button
 * @property {boolean} [disabled] - Whether the picker is disabled
 * @property {Function} [onChange] - Callback when date range changes
 */

/**
 * @typedef {ControlBaseProps & Object} ControlProps
 * Union type of all possible control props based on the type
 */

/**
 * @typedef {Object} ControlComponentProps
 * @property {string} type - Control type: 'input', 'select', 'rate', 'upload', 'datepicker', 'timepicker-range', 'input-multilang', 'textarea', 'input-number', 'datepicker-range'
 * @property {string} [label] - Label text
 * @property {string} [name] - Field name
 * @property {boolean} [required] - Required field
 * @property {string} [placeholder] - Placeholder text
 * @property {boolean} [disabled] - Disabled state
 * @property {any} [value] - Current value
 * @property {Function} [onChange] - Change handler
 * @property {boolean} [loading] - Loading state
 * @property {boolean} [isMobile] - Mobile styling
 * @property {Array} [options] - Select options
 * @property {boolean} [multiple] - Multiple selection
 * @property {string} [format] - Date/time format
 * @property {number} [count] - Star count for rate
 * @property {boolean} [allowHalf] - Half stars for rate
 * @property {boolean} [allowClear] - Clear button
 * @property {number} [min] - Min value
 * @property {number} [max] - Max value
 * @property {number} [step] - Step value
 * @property {number} [rows] - Textarea rows
 * @property {boolean} [autoSize] - Auto resize
 * @property {string} [action] - Upload action
 * @property {Array} [accept] - Accepted file types
 * @property {number} [maxCount] - Max file count
 * @property {Object} [translations] - Multi-language translations
 * @property {Array} [languages] - Supported languages
 * @property {string} [defaultLanguage] - Default language
 */

/**
 * Universal form control component that renders different input types based on the `type` prop.
 * Supports lazy loading for better performance and provides consistent form control interface.
 *
 * @param {ControlComponentProps} props - The control props
 * @returns {JSX.Element} The rendered control component wrapped in Suspense
 *
 * @example
 * // Basic input control
 * <Control
 *   type="input"
 *   label="Username"
 *   name="username"
 *   placeholder="Enter username"
 *   required
 * />
 *
 * @example
 * // Select control with options
 * <Control
 *   type="select"
 *   label="Country"
 *   name="country"
 *   options={[
 *     { label: 'USA', value: 'us' },
 *     { label: 'Canada', value: 'ca' }
 *   ]}
 * />
 *
 * @example
 * // Date picker control
 * <Control
 *   type="datepicker"
 *   label="Birth Date"
 *   name="birthDate"
 *   format="YYYY-MM-DD"
 * />
 *
 * @example
 * // Rate control for ratings
 * <Control
 *   type="rate"
 *   label="Rating"
 *   name="rating"
 *   count={5}
 *   allowHalf
 * />
 */
function Control(props) {
    /**
     * Renders the appropriate control component based on the type prop.
     * Uses a switch statement to determine which lazy-loaded component to render.
     *
     * @private
     * @function renderControl
     * @returns {JSX.Element|null} The rendered control component or null if type is not supported
     */
    const renderControl = () => {
        switch (props.type) {
            case 'input':
                return <Input {...props} />;
            case 'select':
                return <Select {...props} />;
            case 'rate':
                return <Rate {...props} />;
            case 'upload':
                return <Upload {...props} />;
            case 'datepicker':
                return <DatePicker {...props} />;
            case 'timepicker-range':
                return <TimePickerRange {...props} />;
            case 'input-multilang':
                return <InputMultiLang {...props} />;
            case 'textarea':
                return <TextArea {...props} />;
            case 'input-number':
                return <InputNumber {...props} />;
            case 'datepicker-range':
                return <DatePickerRange {...props} />;
            default:
                return null;
        }
    };

    /**
     * Skeleton loading component shown while the control is being lazy-loaded.
     * Maintains consistent layout and styling during loading state.
     *
     * @private
     * @constant {JSX.Element} skeleton
     */
    const skeleton = (
        <Form.Item label={props.label} name={props}>
            <Skeleton width="100%" height={32} style={{ borderRadius: 'var(--border-radius-base)' }} />
        </Form.Item>
    );

    return <Suspense fallback={skeleton}>{renderControl()}</Suspense>;
}

export default Control;
